import { useState, useEffect, useRef, useCallback } from 'react';
import { getProducts, createProducts, updateProduct, deleteProduct, getSuppliers, uploadImage, getUnits, getCategories, importProductsCSV, getCustomers, migrateSalesToProducts } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Save, X, Package, Upload, Trash2, Pencil, Search, FileUp, Download, Users, RefreshCw, Tag } from 'lucide-react';

const emptyRow = { name: '', origin: '', spec: '', price: '', unit: '個', supplier_id: '', image_url: '', description: '', category_id: '', exclusive_customer_ids: [], is_sale: false, is_hidden: false, is_tewari: false, print_group: '通常', delivery_pattern: '通常' };
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// 画像URLをフルURLに変換（iPhone Safari対応）
const getFullImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${API_URL}${url}`;
};

export const Products = () => {
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [units, setUnits] = useState([]);
  const [categories, setCategories] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newRows, setNewRows] = useState([{ ...emptyRow }]);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [csvImporting, setCsvImporting] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [exclusiveDialogOpen, setExclusiveDialogOpen] = useState(false);
  const [selectedExclusiveIds, setSelectedExclusiveIds] = useState([]);
  const [filterSaleOnly, setFilterSaleOnly] = useState(false);
  const fileInputRef = useRef({});
  const csvInputRef = useRef(null);

  useEffect(() => {
    fetchData();
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const checkMobile = () => {
    setIsMobile(window.innerWidth < 768);
  };

  // 特売データ移行
  const handleMigrateSales = async () => {
    if (!window.confirm('旧特売マスタのデータを商品マスタに移行しますか？\n（既存の同名商品は特売フラグがONになります）')) {
      return;
    }
    setMigrating(true);
    try {
      const res = await migrateSalesToProducts();
      toast.success(res.data.message);
      if (res.data.details?.length > 0) {
        console.log('Migration details:', res.data.details);
      }
      fetchData();
    } catch (error) {
      toast.error('移行に失敗しました');
    } finally {
      setMigrating(false);
    }
  };

  const fetchData = async () => {
    try {
      const [productsRes, suppliersRes, unitsRes, categoriesRes, customersRes] = await Promise.all([
        getProducts(), getSuppliers(), getUnits(), getCategories(), getCustomers()
      ]);
      setProducts(productsRes.data);
      setSuppliers(suppliersRes.data);
      setUnits(unitsRes.data);
      setCategories(categoriesRes.data);
      setCustomers(customersRes.data);
    } catch (error) {
      toast.error('データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleNewRowChange = useCallback((index, field, value) => {
    setNewRows(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  }, []);

  const addNewRow = () => {
    setNewRows([...newRows, { ...emptyRow }]);
  };

  const removeNewRow = (index) => {
    if (newRows.length > 1) {
      setNewRows(newRows.filter((_, i) => i !== index));
    }
  };

  const handleFileUpload = async (index, file, isEdit = false) => {
    if (!file) return;
    try {
      const url = await uploadImage(file);
      if (isEdit) {
        setEditData({ ...editData, image_url: url });
      } else {
        handleNewRowChange(index, 'image_url', url);
      }
      toast.success('画像をアップロードしました');
    } catch (error) {
      toast.error('画像のアップロードに失敗しました');
    }
  };

  const handleSaveNew = async () => {
    const validRows = newRows.filter(r => r.name && r.price);
    if (validRows.length === 0) {
      toast.error('商品名と価格は必須です');
      return;
    }

    setSaving(true);
    try {
      const items = validRows.map(r => ({
        ...r,
        price: parseFloat(r.price),
        supplier_id: r.supplier_id || null,
        image_url: r.image_url || null,
        description: r.description || '',
        category_id: r.category_id || null,
      }));
      await createProducts(items);
      toast.success(`${items.length}件の商品を登録しました`);
      setNewRows([{ ...emptyRow }]);
      fetchData();
    } catch (error) {
      toast.error('登録に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  // CSVインポート
  const handleCSVImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setCsvImporting(true);
    try {
      const result = await importProductsCSV(file);
      toast.success(result.message);
      if (result.errors && result.errors.length > 0) {
        result.errors.forEach(err => toast.error(err));
      }
      fetchData();
    } catch (error) {
      toast.error('CSVインポートに失敗しました');
    } finally {
      setCsvImporting(false);
      if (csvInputRef.current) csvInputRef.current.value = '';
    }
  };

  // CSVテンプレートダウンロード
  const downloadCSVTemplate = () => {
    const headers = '商品名,規格,産地,容量,単位,価格,仕入れ先,カテゴリ';
    const example = '【冷凍】明太子大粒,1kg,-,,kg,3000,○○水産,魚介類';
    const content = `${headers}\n${example}`;
    const blob = new Blob(['\uFEFF' + content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = '商品インポートテンプレート.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const startEdit = (product) => {
    setEditingId(product.id);
    setEditData({ ...product });
    if (isMobile) {
      setEditDialogOpen(true);
    }
  };

  // ダブルクリックで編集開始
  const handleDoubleClick = (product) => {
    startEdit(product);
  };

  // 専用顧客設定ダイアログを開く
  const openExclusiveDialog = (product) => {
    setSelectedProduct(product);
    setSelectedExclusiveIds(product.exclusive_customer_ids || []);
    setExclusiveDialogOpen(true);
  };

  // 専用顧客を保存
  const saveExclusiveCustomers = async () => {
    try {
      await updateProduct(selectedProduct.id, { exclusive_customer_ids: selectedExclusiveIds });
      toast.success('専用顧客を更新しました');
      setExclusiveDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('専用顧客の更新に失敗しました');
    }
  };

  // 専用顧客の選択切り替え
  const toggleExclusiveCustomer = (customerId) => {
    if (selectedExclusiveIds.includes(customerId)) {
      setSelectedExclusiveIds(selectedExclusiveIds.filter(id => id !== customerId));
    } else {
      setSelectedExclusiveIds([...selectedExclusiveIds, customerId]);
    }
  };

  // 検索フィルタ
  const filteredProducts = products.filter(p => {
    // 特売のみフィルター
    if (filterSaleOnly && !p.is_sale) return false;
    
    const query = searchQuery.toLowerCase();
    return (
      p.name?.toLowerCase().includes(query) ||
      p.origin?.toLowerCase().includes(query) ||
      p.spec?.toLowerCase().includes(query) ||
      p.supplier_name?.toLowerCase().includes(query)
    );
  });

  // 特売商品の数をカウント
  const saleCount = products.filter(p => p.is_sale).length;

  const cancelEdit = () => {
    setEditingId(null);
    setEditData({});
    setEditDialogOpen(false);
  };

  const saveEdit = async () => {
    try {
      await updateProduct(editingId, {
        name: editData.name,
        origin: editData.origin,
        spec: editData.spec,
        price: parseFloat(editData.price),
        unit: editData.unit,
        supplier_id: editData.supplier_id || null,
        image_url: editData.image_url || null,
        description: editData.description || '',
        category_id: editData.category_id || null,
        print_group: editData.print_group || '通常',
        delivery_pattern: editData.delivery_pattern || '通常',
        is_tewari: editData.is_tewari || false,
      });
      toast.success('更新しました');
      setEditingId(null);
      setEditDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('更新に失敗しました');
    }
  };

  const handleDelete = (product) => {
    setSelectedProduct(product);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await deleteProduct(selectedProduct.id);
      toast.success('削除しました');
      setDeleteDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('削除に失敗しました');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div></div>;
  }

  return (
    <div className="space-y-6 fade-in" data-testid="products-page">
      <div className="page-header">
        <h1 className="page-title">商品マスタ</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" onClick={downloadCSVTemplate} data-testid="download-csv-template-btn">
            <Download className="h-4 w-4 mr-2" />
            CSVテンプレート
          </Button>
          <Button 
            variant="outline" 
            onClick={() => csvInputRef.current?.click()} 
            disabled={csvImporting}
            data-testid="import-csv-btn"
          >
            <FileUp className="h-4 w-4 mr-2" />
            {csvImporting ? 'インポート中...' : 'CSVインポート'}
          </Button>
          <input
            ref={csvInputRef}
            type="file"
            accept=".csv"
            onChange={handleCSVImport}
            className="hidden"
          />
        </div>
      </div>

      {/* 新規登録エリア */}
      <Card className="border-slate-200 border-2 border-dashed">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Plus className="h-5 w-5" />
            新規登録
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* モバイル用フォーム */}
          {isMobile ? (
            <div>
              {newRows.map((row, index) => (
                <Card key={`mobile-new-${index}`} className="mb-4 border-slate-200">
                  <CardContent className="p-4 space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">商品 {index + 1}</span>
                      {newRows.length > 1 && (
                        <Button variant="ghost" size="sm" onClick={() => removeNewRow(index)} className="text-red-600">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs">商品名 *</Label>
                      <Input 
                        defaultValue={row.name} 
                        onBlur={(e) => handleNewRowChange(index, 'name', e.target.value)} 
                        placeholder="商品名" 
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">産地</Label>
                        <Input 
                          defaultValue={row.origin} 
                          onBlur={(e) => handleNewRowChange(index, 'origin', e.target.value)} 
                          placeholder="産地" 
                        />
                      </div>
                      <div>
                        <Label className="text-xs">規格</Label>
                        <Input 
                          defaultValue={row.spec} 
                          onBlur={(e) => handleNewRowChange(index, 'spec', e.target.value)} 
                          placeholder="規格" 
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">価格 *</Label>
                        <Input 
                          type="number" 
                          defaultValue={row.price} 
                          onBlur={(e) => handleNewRowChange(index, 'price', e.target.value)} 
                          placeholder="0" 
                        />
                      </div>
                      <div>
                        <Label className="text-xs">単位</Label>
                        <Select value={row.unit} onValueChange={(v) => handleNewRowChange(index, 'unit', v)}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {units.length > 0 ? (
                              units.map(u => <SelectItem key={u.id} value={u.name}>{u.name}</SelectItem>)
                            ) : (
                              <>
                                <SelectItem value="個">個</SelectItem>
                                <SelectItem value="kg">kg</SelectItem>
                                <SelectItem value="箱">箱</SelectItem>
                              </>
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">仕入れ先</Label>
                      <Select value={row.supplier_id || "none"} onValueChange={(v) => handleNewRowChange(index, 'supplier_id', v === "none" ? "" : v)}>
                        <SelectTrigger><SelectValue placeholder="選択" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">-</SelectItem>
                          {suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">説明</Label>
                      <Input 
                        defaultValue={row.description} 
                        onBlur={(e) => handleNewRowChange(index, 'description', e.target.value)} 
                        placeholder="説明" 
                      />
                    </div>
                    <div>
                      <Label className="text-xs">画像</Label>
                      <div className="flex items-center gap-2">
                        <input type="file" accept="image/*" className="hidden" ref={(el) => fileInputRef.current[`new-mobile-${index}`] = el} onChange={(e) => handleFileUpload(index, e.target.files[0])} />
                        <Button variant="outline" size="sm" onClick={() => fileInputRef.current[`new-mobile-${index}`]?.click()}>
                          <Upload className="h-3 w-3 mr-1" />画像選択
                        </Button>
                        {row.image_url && <img src={row.image_url} alt="" className="h-10 w-10 object-cover rounded" loading="lazy" />}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              <div className="flex gap-2 mt-4">
                <Button variant="outline" onClick={addNewRow} className="flex-1"><Plus className="h-4 w-4 mr-1" />追加</Button>
                <Button onClick={handleSaveNew} disabled={saving} className="flex-1"><Save className="h-4 w-4 mr-1" />{saving ? '登録中...' : '登録'}</Button>
              </div>
            </div>
          ) : (
            /* PC用テーブル */
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2 w-28">商品名 *</th>
                      <th className="text-left p-2 w-20">産地</th>
                      <th className="text-left p-2 w-20">規格</th>
                      <th className="text-left p-2 w-16">価格 *</th>
                      <th className="text-left p-2 w-14">単位</th>
                      <th className="text-left p-2 w-24">カテゴリ</th>
                      <th className="text-left p-2 w-24">仕入れ先</th>
                      <th className="text-left p-2 w-28">説明</th>
                      <th className="text-left p-2 w-20">画像</th>
                      <th className="w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {newRows.map((row, index) => (
                      <tr key={index} className="border-b">
                        <td className="p-1">
                          <Input value={row.name} onChange={(e) => handleNewRowChange(index, 'name', e.target.value)} placeholder="商品名" className="h-8" />
                        </td>
                        <td className="p-1">
                          <Input value={row.origin} onChange={(e) => handleNewRowChange(index, 'origin', e.target.value)} placeholder="産地" className="h-8" />
                        </td>
                        <td className="p-1">
                          <Input value={row.spec} onChange={(e) => handleNewRowChange(index, 'spec', e.target.value)} placeholder="規格" className="h-8" />
                        </td>
                        <td className="p-1">
                          <Input type="number" value={row.price} onChange={(e) => handleNewRowChange(index, 'price', e.target.value)} placeholder="0" className="h-8" />
                        </td>
                        <td className="p-1">
                          <Select value={row.unit} onValueChange={(v) => handleNewRowChange(index, 'unit', v)}>
                            <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {units.length > 0 ? (
                                units.map(u => <SelectItem key={u.id} value={u.name}>{u.name}</SelectItem>)
                              ) : (
                                <>
                                  <SelectItem value="個">個</SelectItem>
                                  <SelectItem value="kg">kg</SelectItem>
                                  <SelectItem value="箱">箱</SelectItem>
                                </>
                              )}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="p-1">
                          <Select value={row.category_id || "none"} onValueChange={(v) => handleNewRowChange(index, 'category_id', v === "none" ? "" : v)}>
                            <SelectTrigger className="h-8"><SelectValue placeholder="選択" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">-</SelectItem>
                              {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="p-1">
                          <Select value={row.supplier_id || "none"} onValueChange={(v) => handleNewRowChange(index, 'supplier_id', v === "none" ? "" : v)}>
                            <SelectTrigger className="h-8"><SelectValue placeholder="選択" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">-</SelectItem>
                              {suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="p-1">
                          <Input value={row.description} onChange={(e) => handleNewRowChange(index, 'description', e.target.value)} placeholder="説明" className="h-8" />
                        </td>
                        <td className="p-1">
                          <div className="flex items-center gap-1">
                            <input type="file" accept="image/*" className="hidden" ref={(el) => fileInputRef.current[`new-${index}`] = el} onChange={(e) => handleFileUpload(index, e.target.files[0])} />
                            <Button variant="outline" size="sm" className="h-8 px-2" onClick={() => fileInputRef.current[`new-${index}`]?.click()}>
                              <Upload className="h-3 w-3" />
                            </Button>
                            {row.image_url && <img src={row.image_url} alt="" className="h-8 w-8 object-cover rounded" loading="lazy" />}
                          </div>
                        </td>
                        <td className="p-1">
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => removeNewRow(index)} disabled={newRows.length === 1}>
                            <X className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex gap-2 mt-4">
                <Button variant="outline" onClick={addNewRow}><Plus className="h-4 w-4 mr-1" />行を追加</Button>
                <Button onClick={handleSaveNew} disabled={saving}><Save className="h-4 w-4 mr-1" />{saving ? '登録中...' : '一括登録'}</Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* 既存商品一覧 */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-lg">登録済み商品 ({filteredProducts.length}件)</CardTitle>
            <div className="flex items-center gap-2 flex-wrap">
              {/* 特売のみフィルター */}
              <Button
                variant={filterSaleOnly ? "default" : "outline"}
                size="sm"
                onClick={() => setFilterSaleOnly(!filterSaleOnly)}
                className={filterSaleOnly ? "bg-red-500 hover:bg-red-600" : "border-red-400 text-red-600 hover:bg-red-50"}
                data-testid="filter-sale-only-btn"
              >
                <Tag className="h-4 w-4 mr-1" />
                特売のみ ({saleCount}件)
              </Button>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="検索..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 w-48"
                  data-testid="product-search"
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {filteredProducts.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Package className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p>{searchQuery ? '検索結果がありません' : '商品がありません'}</p>
            </div>
          ) : isMobile ? (
            /* モバイル用カード表示 */
            <div className="p-4 space-y-3">
              {filteredProducts.map((product) => (
                <Card key={product.id} className={`border ${product.is_sale ? 'border-red-400 bg-red-50' : ''} ${product.is_hidden ? 'opacity-50' : ''}`} onDoubleClick={() => handleDoubleClick(product)}>
                  <CardContent className="p-3">
                    <div className="flex items-start gap-3">
                      <div className="w-16 h-16 bg-slate-100 rounded overflow-hidden flex-shrink-0">
                        {product.image_url ? <img src={getFullImageUrl(product.image_url)} alt="" className="w-full h-full object-cover" loading="lazy" /> : <div className="w-full h-full flex items-center justify-center"><Package className="h-6 w-6 text-slate-300" /></div>}
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="font-medium">{product.name}</span>
                        <p className="text-sm text-slate-500">{product.origin || '-'} / {product.spec || '-'}</p>
                        <p className="font-bold">{product.price === 0 ? '時価' : `¥${product.price?.toLocaleString()}/${product.unit}`}</p>
                      </div>
                    </div>
                    {/* 特売・非表示フラグ */}
                    <div className="flex gap-4 mt-3 pt-3 border-t">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <Checkbox
                          checked={product.is_sale || false}
                          onCheckedChange={(checked) => {
                            setProducts(prev => prev.map(p => 
                              p.id === product.id ? { ...p, is_sale: checked } : p
                            ));
                            updateProduct(product.id, { is_sale: checked }).catch(() => {
                              setProducts(prev => prev.map(p => 
                                p.id === product.id ? { ...p, is_sale: !checked } : p
                              ));
                              toast.error('更新に失敗しました', { duration: 1500 });
                            });
                          }}
                          className={product.is_sale ? 'border-red-500 data-[state=checked]:bg-red-500' : ''}
                        />
                        <span className="text-sm text-red-600 font-medium">特売</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <Checkbox
                          checked={product.is_hidden || false}
                          onCheckedChange={(checked) => {
                            setProducts(prev => prev.map(p => 
                              p.id === product.id ? { ...p, is_hidden: checked } : p
                            ));
                            updateProduct(product.id, { is_hidden: checked }).catch(() => {
                              setProducts(prev => prev.map(p => 
                                p.id === product.id ? { ...p, is_hidden: !checked } : p
                              ));
                              toast.error('更新に失敗しました', { duration: 1500 });
                            });
                          }}
                        />
                        <span className="text-sm text-slate-600">非表示</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <Checkbox
                          checked={product.is_tewari || false}
                          onCheckedChange={(checked) => {
                            setProducts(prev => prev.map(p => 
                              p.id === product.id ? { ...p, is_tewari: checked } : p
                            ));
                            updateProduct(product.id, { is_tewari: checked }).catch(() => {
                              setProducts(prev => prev.map(p => 
                                p.id === product.id ? { ...p, is_tewari: !checked } : p
                              ));
                              toast.error('更新に失敗しました', { duration: 1500 });
                            });
                          }}
                          className="border-orange-500 data-[state=checked]:bg-orange-500"
                        />
                        <span className="text-sm text-orange-600">手割り</span>
                      </label>
                      <select
                        value={product.print_group || '通常'}
                        onChange={(e) => {
                          const val = e.target.value;
                          setProducts(prev => prev.map(p => p.id === product.id ? { ...p, print_group: val } : p));
                          updateProduct(product.id, { print_group: val }).catch(() => {
                            setProducts(prev => prev.map(p => p.id === product.id ? { ...p, print_group: product.print_group || '通常' } : p));
                            toast.error('更新に失敗しました', { duration: 1500 });
                          });
                        }}
                        className="text-xs border rounded px-2 py-1 bg-white"
                      >
                        <option value="通常">通常</option>
                        <option value="お米">お米</option>
                      </select>
                    </div>
                    <div className="flex gap-2 mt-3 pt-3 border-t">
                      <Button variant="outline" size="sm" className="flex-1 touch-manipulation" onClick={() => startEdit(product)}>
                        <Pencil className="h-4 w-4 mr-1" />編集
                      </Button>
                      <Button variant="outline" size="sm" className="text-red-600 touch-manipulation" onClick={() => handleDelete(product)}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            /* PC用テーブル表示 */
            <div className="overflow-x-auto">
              <table className="data-table" style={{minWidth: '1400px'}}>
                <thead>
                  <tr>
                    <th>画像</th>
                    <th>商品名</th>
                    <th>産地</th>
                    <th>規格</th>
                    <th>価格</th>
                    <th>単位</th>
                    <th>カテゴリ</th>
                    <th>仕入れ先</th>
                    <th>特売</th>
                    <th>非表示</th>
                    <th>手割り</th>
                    <th>帳票</th>
                    <th>配送</th>
                    <th>専用顧客</th>
                    <th>説明</th>
                    <th className="text-right">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.map((product) => (
                    <tr 
                      key={product.id} 
                      onDoubleClick={() => handleDoubleClick(product)}
                      className={`${editingId !== product.id ? 'cursor-pointer hover:bg-slate-50' : ''} ${product.is_sale ? 'bg-red-50' : ''} ${product.is_hidden ? 'opacity-50' : ''}`}
                      title={editingId !== product.id ? 'ダブルクリックで編集' : ''}
                    >
                      {editingId === product.id ? (
                        <>
                          <td className="p-2">
                            <div className="flex items-center gap-1">
                              <input type="file" accept="image/*" className="hidden" ref={(el) => fileInputRef.current[`edit-${product.id}`] = el} onChange={(e) => handleFileUpload(0, e.target.files[0], true)} />
                              <Button variant="outline" size="sm" className="h-8 px-2" onClick={() => fileInputRef.current[`edit-${product.id}`]?.click()}>
                                <Upload className="h-3 w-3" />
                              </Button>
                              {editData.image_url && <img src={editData.image_url} alt="" className="h-8 w-8 object-cover rounded" loading="lazy" />}
                            </div>
                          </td>
                          <td className="p-1"><Input value={editData.name} onChange={(e) => setEditData({...editData, name: e.target.value})} className="h-8" /></td>
                          <td className="p-1"><Input value={editData.origin} onChange={(e) => setEditData({...editData, origin: e.target.value})} className="h-8" /></td>
                          <td className="p-1"><Input value={editData.spec} onChange={(e) => setEditData({...editData, spec: e.target.value})} className="h-8" /></td>
                          <td className="p-1"><Input type="number" value={editData.price} onChange={(e) => setEditData({...editData, price: e.target.value})} className="h-8 w-20" /></td>
                          <td className="p-1">
                            <Select value={editData.unit} onValueChange={(v) => setEditData({...editData, unit: v})}>
                              <SelectTrigger className="h-8 w-16"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {units.length > 0 ? (
                                  units.map(u => <SelectItem key={u.id} value={u.name}>{u.name}</SelectItem>)
                                ) : (
                                  <>
                                    <SelectItem value="個">個</SelectItem>
                                    <SelectItem value="kg">kg</SelectItem>
                                    <SelectItem value="箱">箱</SelectItem>
                                  </>
                                )}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="p-1">
                            <Select value={editData.category_id || "none"} onValueChange={(v) => setEditData({...editData, category_id: v === "none" ? null : v})}>
                              <SelectTrigger className="h-8"><SelectValue placeholder="選択" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">-</SelectItem>
                                {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="p-1">
                            <Select value={editData.supplier_id || "none"} onValueChange={(v) => setEditData({...editData, supplier_id: v === "none" ? "" : v})}>
                              <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">-</SelectItem>
                                {suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="p-1"><Input value={editData.description || ''} onChange={(e) => setEditData({...editData, description: e.target.value})} className="h-8" /></td>
                          <td colSpan="2" className="p-1">
                            <div className="flex gap-1 items-center">
                              <select value={editData.print_group || '通常'} onChange={(e) => setEditData({...editData, print_group: e.target.value})} className="text-xs border rounded px-1 py-1 bg-white h-8">
                                <option value="通常">通常</option>
                                <option value="お米">お米</option>
                              </select>
                              <select value={editData.delivery_pattern || '通常'} onChange={(e) => setEditData({...editData, delivery_pattern: e.target.value})} className="text-xs border rounded px-1 py-1 bg-white h-8">
                                <option value="通常">通常</option>
                                <option value="中1日">中1日</option>
                                <option value="週次水曜締め">週次水曜</option>
                              </select>
                            </div>
                          </td>
                          <td className="text-right p-2">
                            <Button size="sm" onClick={saveEdit} className="mr-1">保存</Button>
                            <Button size="sm" variant="outline" onClick={cancelEdit}>取消</Button>
                          </td>
                        </>
                      ) : (
                        <>
                          <td>
                            <div className="w-10 h-10 bg-slate-100 rounded overflow-hidden">
                              {product.image_url ? <img src={getFullImageUrl(product.image_url)} alt="" className="w-full h-full object-cover" loading="lazy" /> : <div className="w-full h-full flex items-center justify-center"><Package className="h-5 w-5 text-slate-300" /></div>}
                            </div>
                          </td>
                          <td className="font-medium">{product.name}</td>
                          <td>{product.origin || '-'}</td>
                          <td>{product.spec || '-'}</td>
                          <td className="font-mono">{product.price === 0 ? '時価' : `¥${product.price?.toLocaleString()}`}</td>
                          <td>{product.unit}</td>
                          <td>{product.category_name || '-'}</td>
                          <td>{product.supplier_name || '-'}</td>
                          <td>
                            <Checkbox
                              checked={product.is_sale || false}
                              onCheckedChange={(checked) => {
                                // 楽観的更新：即座にUIを更新
                                setProducts(prev => prev.map(p => 
                                  p.id === product.id ? { ...p, is_sale: checked } : p
                                ));
                                // バックグラウンドでAPI呼び出し
                                updateProduct(product.id, { is_sale: checked }).catch(() => {
                                  // エラー時は元に戻す
                                  setProducts(prev => prev.map(p => 
                                    p.id === product.id ? { ...p, is_sale: !checked } : p
                                  ));
                                  toast.error('更新に失敗しました', { duration: 1500 });
                                });
                              }}
                              className={product.is_sale ? 'border-red-500 data-[state=checked]:bg-red-500' : ''}
                            />
                          </td>
                          <td>
                            <Checkbox
                              checked={product.is_hidden || false}
                              onCheckedChange={(checked) => {
                                setProducts(prev => prev.map(p => 
                                  p.id === product.id ? { ...p, is_hidden: checked } : p
                                ));
                                updateProduct(product.id, { is_hidden: checked }).catch(() => {
                                  setProducts(prev => prev.map(p => 
                                    p.id === product.id ? { ...p, is_hidden: !checked } : p
                                  ));
                                  toast.error('更新に失敗しました', { duration: 1500 });
                                });
                              }}
                            />
                          </td>
                          <td>
                            <Checkbox
                              checked={product.is_tewari || false}
                              onCheckedChange={(checked) => {
                                setProducts(prev => prev.map(p => 
                                  p.id === product.id ? { ...p, is_tewari: checked } : p
                                ));
                                updateProduct(product.id, { is_tewari: checked }).catch(() => {
                                  setProducts(prev => prev.map(p => 
                                    p.id === product.id ? { ...p, is_tewari: !checked } : p
                                  ));
                                  toast.error('更新に失敗しました', { duration: 1500 });
                                });
                              }}
                            />
                          </td>
                          <td>
                            <select
                              value={product.print_group || '通常'}
                              onChange={(e) => {
                                const val = e.target.value;
                                setProducts(prev => prev.map(p => p.id === product.id ? { ...p, print_group: val } : p));
                                updateProduct(product.id, { print_group: val }).catch(() => {
                                  setProducts(prev => prev.map(p => p.id === product.id ? { ...p, print_group: product.print_group || '通常' } : p));
                                  toast.error('更新に失敗しました', { duration: 1500 });
                                });
                              }}
                              className={`text-xs border rounded px-2 py-1 bg-white ${product.print_group === 'お米' ? 'border-amber-400 text-amber-700 font-bold' : ''}`}
                            >
                              <option value="通常">通常</option>
                              <option value="お米">お米</option>
                            </select>
                          </td>
                          <td>
                            <select
                              value={product.delivery_pattern || '通常'}
                              onChange={(e) => {
                                const val = e.target.value;
                                setProducts(prev => prev.map(p => p.id === product.id ? { ...p, delivery_pattern: val } : p));
                                updateProduct(product.id, { delivery_pattern: val }).catch(() => {
                                  setProducts(prev => prev.map(p => p.id === product.id ? { ...p, delivery_pattern: product.delivery_pattern || '通常' } : p));
                                  toast.error('更新に失敗しました', { duration: 1500 });
                                });
                              }}
                              className={`text-xs border rounded px-2 py-1 bg-white ${product.delivery_pattern !== '通常' && product.delivery_pattern ? 'border-purple-400 text-purple-700 font-bold' : ''}`}
                            >
                              <option value="通常">通常</option>
                              <option value="中1日">中1日</option>
                              <option value="週次水曜締め">週次水曜締め</option>
                            </select>
                          </td>
                          <td>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openExclusiveDialog(product)}
                              className={product.exclusive_customer_ids?.length > 0 ? 'border-amber-400 text-amber-600' : ''}
                            >
                              <Users className="h-3 w-3 mr-1" />
                              {product.exclusive_customer_ids?.length > 0 
                                ? `${product.exclusive_customer_ids.length}件` 
                                : '全員'}
                            </Button>
                          </td>
                          <td className="max-w-32 truncate" title={product.description}>{product.description || '-'}</td>
                          <td className="text-right">
                            <Button variant="ghost" size="sm" onClick={() => startEdit(product)}>編集</Button>
                            <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDelete(product)}>削除</Button>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* 専用顧客設定ダイアログ */}
      <Dialog open={exclusiveDialogOpen} onOpenChange={setExclusiveDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>🔒 専用顧客の設定</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-slate-500 mb-2">
              「{selectedProduct?.name}」を表示する顧客を選択
            </p>
            <p className="text-xs text-slate-400 mb-4">
              ※ 選択した顧客のみにこの商品が表示されます<br />
              ※ 選択しない場合は全顧客に表示されます
            </p>
            <div className="flex gap-2 mb-4">
              <Button variant="outline" size="sm" onClick={() => setSelectedExclusiveIds(customers.map(c => c.id))}>全て選択</Button>
              <Button variant="outline" size="sm" onClick={() => setSelectedExclusiveIds([])}>全て解除（全員表示）</Button>
            </div>
            <div className="space-y-2 max-h-64 overflow-y-auto border rounded-md p-3">
              {customers.length === 0 ? (
                <p className="text-center text-slate-400 py-4">取引先がありません</p>
              ) : (
                customers.map((customer) => (
                  <div key={customer.id} className="flex items-center space-x-3 py-2 border-b last:border-b-0">
                    <Checkbox
                      id={`exclusive-${customer.id}`}
                      checked={selectedExclusiveIds.includes(customer.id)}
                      onCheckedChange={() => toggleExclusiveCustomer(customer.id)}
                    />
                    <label htmlFor={`exclusive-${customer.id}`} className="flex-1 cursor-pointer">
                      <span className="font-medium">{customer.name}</span>
                      <span className="text-sm text-slate-400 ml-2">{customer.code}</span>
                    </label>
                  </div>
                ))
              )}
            </div>
            <p className="text-sm text-slate-500 mt-2">
              {selectedExclusiveIds.length > 0 
                ? `🔒 ${selectedExclusiveIds.length}件の顧客のみ表示` 
                : '🌐 全顧客に表示'}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExclusiveDialogOpen(false)}>キャンセル</Button>
            <Button onClick={saveExclusiveCustomers}>保存</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 削除確認 */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>商品を削除しますか？</AlertDialogTitle>
            <AlertDialogDescription>
              「{selectedProduct?.name}」を削除します。この操作は取り消せません。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>キャンセル</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">削除</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* モバイル用編集ダイアログ */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>商品編集</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>商品名 *</Label>
              <Input 
                value={editData.name || ''} 
                onChange={(e) => setEditData({...editData, name: e.target.value})} 
                placeholder="商品名" 
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>産地</Label>
                <Input 
                  value={editData.origin || ''} 
                  onChange={(e) => setEditData({...editData, origin: e.target.value})} 
                  placeholder="産地" 
                />
              </div>
              <div>
                <Label>規格</Label>
                <Input 
                  value={editData.spec || ''} 
                  onChange={(e) => setEditData({...editData, spec: e.target.value})} 
                  placeholder="規格" 
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>価格 *</Label>
                <Input 
                  type="number" 
                  value={editData.price || ''} 
                  onChange={(e) => setEditData({...editData, price: e.target.value})} 
                  placeholder="0" 
                />
              </div>
              <div>
                <Label>単位</Label>
                <Select value={editData.unit || '個'} onValueChange={(v) => setEditData({...editData, unit: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {units.length > 0 ? (
                      units.map(u => <SelectItem key={u.id} value={u.name}>{u.name}</SelectItem>)
                    ) : (
                      <>
                        <SelectItem value="個">個</SelectItem>
                        <SelectItem value="kg">kg</SelectItem>
                        <SelectItem value="箱">箱</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>カテゴリ</Label>
              <Select value={editData.category_id || "none"} onValueChange={(v) => setEditData({...editData, category_id: v === "none" ? null : v})}>
                <SelectTrigger><SelectValue placeholder="選択" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-</SelectItem>
                  {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>仕入れ先</Label>
              <Select value={editData.supplier_id || "none"} onValueChange={(v) => setEditData({...editData, supplier_id: v === "none" ? "" : v})}>
                <SelectTrigger><SelectValue placeholder="選択" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-</SelectItem>
                  {suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>説明</Label>
              <Input 
                value={editData.description || ''} 
                onChange={(e) => setEditData({...editData, description: e.target.value})} 
                placeholder="説明" 
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>帳票グループ</Label>
                <select value={editData.print_group || '通常'} onChange={(e) => setEditData({...editData, print_group: e.target.value})} className="w-full border rounded px-3 py-2 bg-white text-sm">
                  <option value="通常">通常</option>
                  <option value="お米">お米</option>
                </select>
              </div>
              <div>
                <Label>配送パターン</Label>
                <select value={editData.delivery_pattern || '通常'} onChange={(e) => setEditData({...editData, delivery_pattern: e.target.value})} className="w-full border rounded px-3 py-2 bg-white text-sm">
                  <option value="通常">通常</option>
                  <option value="中1日">中1日</option>
                  <option value="週次水曜締め">週次水曜締め</option>
                </select>
              </div>
            </div>
            <div>
              <Label>画像</Label>
              <div className="flex items-center gap-2">
                <input type="file" accept="image/*" className="hidden" ref={(el) => fileInputRef.current['edit-mobile'] = el} onChange={(e) => handleFileUpload(0, e.target.files[0], true)} />
                <Button variant="outline" size="sm" onClick={() => fileInputRef.current['edit-mobile']?.click()}>
                  <Upload className="h-3 w-3 mr-1" />画像選択
                </Button>
                {editData.image_url && <img src={editData.image_url} alt="" className="h-10 w-10 object-cover rounded" loading="lazy" />}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={cancelEdit}>キャンセル</Button>
            <Button onClick={saveEdit}>保存</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
